/**
 * Regulatory Updates feed widget — shown on the dashboard for Practitioner+
 * firms. Lists the most recent FCA Handbook changes relevant to the firm's
 * `firm_type`, with mark-as-read + action-note workflow.
 */
import React, { useEffect, useState } from "react";
import { BookOpen, Loader2, CheckCircle2, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

export default function RegulatoryUpdates() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/api/regulatory/updates?limit=10"); setData(data); }
    catch { setData({ items: [] }); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id, note) => {
    setBusy(id);
    try { await api.post(`/api/regulatory/updates/${id}/read`, { action_note: note || null }); await load(); }
    catch (e) { alert(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (!data) return <div className="card2 p-6 text-center"><Loader2 className="animate-spin mx-auto text-slate2" size={16}/></div>;
  if (data.plan_gated) {
    return (
      <div className="card2 p-5">
        <div className="flex items-center gap-2 mb-1"><BookOpen size={16} className="text-indigo2"/><h3 className="t-h4">FCA Handbook updates</h3></div>
        <p className="t-body-sm text-slate2">{data.message}</p>
      </div>
    );
  }
  if (data.items.length === 0) {
    return (
      <div className="card2 p-5" data-testid="regulatory-empty">
        <div className="flex items-center gap-2 mb-1"><BookOpen size={16} className="text-indigo2"/><h3 className="t-h4">FCA Handbook updates</h3></div>
        <p className="t-body-sm text-slate2">No changes detected since you joined Compliche. We check daily.</p>
      </div>
    );
  }

  return (
    <div className="card2 p-5" data-testid="regulatory-updates-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><BookOpen size={16} className="text-indigo2"/><h3 className="t-h4">FCA Handbook updates</h3></div>
        <span className="t-caption">{data.items.filter(i => !i.read).length} unread</span>
      </div>
      <div className="space-y-3">
        {data.items.map((u, idx) => (
          <div key={u.id} id={`regulatory-update-${u.id}`} className={`rounded-lg border ${u.read ? "border-border2 bg-bg2/30" : "border-indigo2/30 bg-indigo2/5"} p-3`}>
            <button onClick={() => setExpanded(p => ({...p, [u.id]: !p[u.id]}))} className="w-full flex items-start justify-between text-left">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                {expanded[u.id] ? <ChevronDown size={14} className="mt-1 flex-shrink-0"/> : <ChevronRight size={14} className="mt-1 flex-shrink-0"/>}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm">{u.section_key}</span>
                    {u.read && <CheckCircle2 size={12} className="text-emerald2"/>}
                  </div>
                  <p className="t-body-sm text-slate2 mt-0.5 line-clamp-2">{u.summary}</p>
                  <div className="t-caption mt-1">{new Date(u.detected_at).toLocaleDateString()}</div>
                </div>
              </div>
            </button>
            {expanded[u.id] && (
              <div className="mt-3 pl-6 space-y-2">
                <p className="t-body-sm">{u.summary}</p>
                {u.added?.length > 0 && <div className="t-caption"><strong className="text-emerald2">Added:</strong> {u.added.slice(0,3).join(" · ")}…</div>}
                {u.removed?.length > 0 && <div className="t-caption"><strong className="text-rose-600">Removed:</strong> {u.removed.slice(0,3).join(" · ")}…</div>}
                <a href={u.url} target="_blank" rel="noreferrer" className="t-caption text-indigo2 hover:underline inline-flex items-center gap-1"><ExternalLink size={11}/>View on FCA site</a>
                {!u.read && (
                  <div className="mt-3 pt-3 border-t border-border2/50">
                    <ActionNoteInput onSubmit={(note) => markRead(u.id, note)} busy={busy === u.id} />
                  </div>
                )}
                {u.read && u.action_note && (
                  <div className="mt-2 t-caption text-slate2"><strong>Action taken:</strong> {u.action_note}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActionNoteInput({ onSubmit, busy }) {
  const [note, setNote] = useState("");
  return (
    <div>
      <textarea value={note} onChange={(e)=>setNote(e.target.value)} placeholder="What action did you take in response? (optional)" className="input2 w-full text-sm" rows={2} data-testid="reg-action-note" />
      <div className="flex gap-2 mt-2">
        <button onClick={() => onSubmit(note)} disabled={busy} className="btn btn-secondary btn-sm" data-testid="reg-mark-read">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <CheckCircle2 size={12}/>}Mark as read
        </button>
        {note && <button onClick={() => onSubmit("")} className="btn btn-ghost btn-sm">Skip note</button>}
      </div>
    </div>
  );
}
